import * as Crypto from 'expo-crypto'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from 'react'
import { Platform } from 'react-native'

import { Toast, type TToastProps } from './Toast'

type TToast = Omit<TToastProps, 'removeToast'>

type TToastContext = {
  toasts: TToast[]
  addToast: (toastProps: Omit<TToast, 'id'>) => string
  removeToast: (id: string) => void
}

export const ToastContext = createContext<TToastContext>({
  toasts: [],
  addToast: () => {
    throw new Error(
      'Cannot add toasts: toast context is not provided. Seems you forgot to wrap component within <ToastProvider />',
    )
  },
  removeToast: () => {
    throw new Error(
      'Cannot remove toast: toast context is not provided. Seems you forgot to wrap component within <ToastProvider />',
    )
  },
})

/**
 * ToastContext Provider
 *
 * Provides the toast messages
 */
export const ToastProvider = ({ children }: PropsWithChildren): ReactNode => {
  const [toasts, setToasts] = useState<TToast[]>([])

  const addToast = useCallback((toastInfos: Omit<TToast, 'id'>) => {
    const id = createToastId()
    setToasts((previousToasts) => [...previousToasts, { id, ...toastInfos }])
    return id
  }, [])

  const removeToast = (id: string) => {
    setToasts((previousToasts) => previousToasts.filter((toast) => toast.id !== id))
  }

  const value = useMemo(() => ({ toasts, addToast, removeToast }), [toasts, addToast])

  return (
    <ToastContext.Provider value={value}>
      {toasts.length > 0 && <Toast key={`toast-${toasts[0].id}`} {...toasts[0]} removeToast={removeToast} />}
      {children}
    </ToastContext.Provider>
  )
}

export const useToast = () => useContext(ToastContext)

const createToastId = (): string => {
  if (Platform.OS !== 'web') {
    return Crypto.randomUUID()
  }
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback for browsers without crypto.randomUUID (e.g. Firefox < 95)
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
  )
}
